import { useEffect, useRef, useState } from 'react'

interface OrderableListProps extends React.HTMLAttributes<HTMLDivElement> {
  children: JSX.Element[]
  onOrderChangeKey?: (orderedChildrenKeys: string[]) => void
  onDragStartKey?: (key: string) => void
  onDragEndKey?: () => void
}

export function OrderableList({
  children,
  onOrderChangeKey,
  onDragStartKey,
  onDragEndKey,
  ...props
}: OrderableListProps) {
  const [orderedChildren, setOrderedChildren] = useState(children)
  const currentDraggedItemIndex = useRef<number | null>(null)

  useEffect(() => {
    setOrderedChildren(children)
  }, [children])

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number
  ) => {
    currentDraggedItemIndex.current = index
    onDragStartKey?.(orderedChildren[index].key! as string)
  }

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    index: number
  ) => {
    e.preventDefault()
    const newOrderedChildren = orderedChildren.slice()
    const draggedItem = newOrderedChildren.splice(
      currentDraggedItemIndex.current!,
      1
    )[0]
    newOrderedChildren.splice(index, 0, draggedItem)
    setOrderedChildren(newOrderedChildren)

    currentDraggedItemIndex.current = index
  }

  const handleDragEnd = () => {
    currentDraggedItemIndex.current = null
    onOrderChangeKey?.(orderedChildren.map((child) => child.key! as string))
    onDragEndKey?.()
  }

  return (
    <div {...props}>
      {orderedChildren?.map((child, index) => (
        <div
          key={child.key}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
        >
          {child}
        </div>
      ))}
    </div>
  )
}
